import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { cls, isOneOf } from '@arco-design/mobile-utils';
import { ContextLayout } from '../context-provider';
import { getStyleWithVendor, useGenSvgKey } from '../_helpers';

/**
 * 加载中组件，分为四种类型，`circle`为环形，`arc`为弧线，`spin`为旋转，`dot`为圆点。所有类型均可定制颜色，环形和弧线类型可定制线圈半径及粗细，旋转和圆点类型可定制内部元素透明度。
 * @en Loading component, divided into four types, `circle` is a ring, `arc` is an arc `spin` is a rotation, and `dot` is a dot. All types can be customized in color, ring and arc types can be customized with coil radius and thickness, and rotation and dot types can be customized with internal element transparency.
 * @type 反馈
 * @type_en Feedback
 * @name 加载
 * @name_en Loading
 */
var Loading = /*#__PURE__*/forwardRef(function (props, ref) {
  var style = props.style,
      _props$className = props.className,
      className = _props$className === void 0 ? '' : _props$className,
      color = props.color,
      _props$type = props.type,
      type = _props$type === void 0 ? 'dot' : _props$type,
      list = props.list,
      _props$duration = props.duration,
      duration = _props$duration === void 0 ? 1000 : _props$duration,
      _props$svgKey = props.svgKey,
      userSetSvgKey = _props$svgKey === void 0 ? '' : _props$svgKey,
      _props$radius = props.radius,
      radius = _props$radius === void 0 ? 9 : _props$radius,
      _props$stroke = props.stroke,
      stroke = _props$stroke === void 0 ? 2 : _props$stroke,
      _props$filleted = props.filleted,
      filleted = _props$filleted === void 0 ? true : _props$filleted;

  var _useState = useState([]),
      statusList = _useState[0],
      setStatusList = _useState[1];

  var timerId = useRef(-1);
  var halfCircle = Math.PI * radius;
  var circlePos = 0.5 * stroke + radius;
  var circleSize = radius * 2 + stroke;
  var domRef = useRef(null);

  var _useGenSvgKey = useGenSvgKey(userSetSvgKey),
      svgKey = _useGenSvgKey.svgKey;

  useImperativeHandle(ref, function () {
    return {
      dom: domRef.current
    };
  });
  useEffect(function () {
    if (type === 'dot' && statusList.length) {
      var interval = duration / statusList.length;
      timerId.current = setInterval(function () {
        var newList = [].concat(statusList);
        var item = newList.pop();
        item && newList.unshift(item);
        setStatusList(newList);
      }, interval);
    }

    return function () {
      if (timerId.current >= 0) {
        clearInterval(timerId.current);
      }
    };
  }, [type, statusList, duration]);
  useEffect(function () {
    var newList;

    if (list && list.length) {
      newList = list;
    } else {
      switch (type) {
        case 'spin':
          newList = [1, 0.1, 0.2286, 0.3572, 0.4858, 0.6144, 0.743, 0.8716];
          break;

        case 'dot':
          newList = [0.2, 0.6, 1];
          break;

        default:
          newList = [];
          break;
      }
    }

    setStatusList(newList);
  }, [list, type]);

  function renderSpin() {
    var len = statusList.length;
    return statusList.map(function (opacity, index) {
      return /*#__PURE__*/React.createElement("span", {
        key: index,
        className: "spin-cell",
        style: getStyleWithVendor({
          opacity: opacity,
          transform: "rotate(" + index / len + "turn)",
          width: stroke
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "spin-cell-inner bg-color-with-config",
        style: _extends({
          backgroundColor: color
        }, filleted ? {
          borderRadius: stroke
        } : {})
      }));
    });
  }

  function renderDot() {
    return statusList.map(function (opacity, index) {
      return /*#__PURE__*/React.createElement("span", {
        key: index,
        className: cls('dot-cell', 'bg-color-with-config', {
          filleted: filleted
        }),
        style: {
          opacity: opacity,
          backgroundColor: color
        }
      });
    });
  }

  function renderCircle() {
    return /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 " + circleSize + " " + circleSize
    }, /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
      id: "grad1-" + svgKey,
      x1: "0%",
      y1: "0%",
      x2: "100%",
      y2: "0%"
    }, /*#__PURE__*/React.createElement("stop", {
      offset: "0%",
      className: "loading-circle-middle stop-color-with-config",
      style: {
        stopColor: color
      }
    }), /*#__PURE__*/React.createElement("stop", {
      offset: "100%",
      className: "loading-circle-start stop-color-with-config",
      style: {
        stopColor: color
      }
    })), /*#__PURE__*/React.createElement("linearGradient", {
      id: "grad2-" + svgKey,
      x1: "0%",
      y1: "0%",
      x2: "100%",
      y2: "0%"
    }, /*#__PURE__*/React.createElement("stop", {
      offset: "0%",
      className: "loading-circle-middle stop-color-with-config",
      style: {
        stopColor: color
      }
    }), /*#__PURE__*/React.createElement("stop", {
      offset: "100%",
      className: "loading-circle-end stop-color-with-config",
      style: {
        stopColor: color
      }
    }))), /*#__PURE__*/React.createElement("circle", {
      cx: circlePos,
      cy: circlePos,
      r: radius,
      stroke: "url(#grad1-" + svgKey + ")",
      strokeWidth: stroke,
      strokeDasharray: halfCircle,
      strokeDashoffset: halfCircle,
      fill: "none"
    }), /*#__PURE__*/React.createElement("circle", {
      cx: circlePos,
      cy: circlePos,
      r: radius,
      stroke: "url(#grad2-" + svgKey + ")",
      strokeWidth: stroke,
      strokeDasharray: halfCircle,
      fill: "none"
    }), filleted ? /*#__PURE__*/React.createElement("circle", {
      cx: circlePos * 2 - stroke / 2,
      cy: circlePos,
      r: stroke / 2,
      className: "loading-circle-filleted fill-color-with-config",
      style: {
        fill: color
      }
    }) : null);
  }

  function renderArc() {
    return /*#__PURE__*/React.createElement("svg", {
      viewBox: "0 0 " + circleSize + " " + circleSize
    }, /*#__PURE__*/React.createElement("circle", {
      className: "arc-bg",
      cx: circlePos,
      cy: circlePos,
      r: radius,
      strokeWidth: stroke,
      fill: "none"
    }), /*#__PURE__*/React.createElement("circle", _extends({
      className: "arc-line stroke-color-with-config",
      cx: circlePos,
      cy: circlePos,
      r: radius,
      style: {
        stroke: color
      },
      strokeWidth: stroke,
      strokeDashoffset: halfCircle * 0.5,
      strokeDasharray: halfCircle * 0.5 + " " + halfCircle * 1.5,
      fill: "none"
    }, filleted ? {
      strokeLinecap: 'round'
    } : {})));
  }

  function renderLoadingByType() {
    switch (type) {
      case 'spin':
        return renderSpin();

      case 'dot':
        return renderDot();

      case 'circle':
        return renderCircle();

      case 'arc':
        return renderArc();

      default:
        return null;
    }
  }

  function getLoadingStyle() {
    var circleStyle = isOneOf(type, ['circle', 'arc']) ? {
      width: circleSize,
      height: circleSize
    } : {};
    return getStyleWithVendor(_extends({
      animationDuration: duration + "ms"
    }, circleStyle, style || {}));
  }

  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls;
    return /*#__PURE__*/React.createElement("div", {
      className: cls(prefixCls + "-loading all-border-box", type, className),
      style: getLoadingStyle(),
      ref: domRef
    }, renderLoadingByType());
  });
});
export default Loading;